import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Header from './components/Header';
import CameraFab from './components/CameraFab';
import BuildStamp from './components/BuildStamp';
import BackendBadge from './components/BackendBadge';
import ClockBar from './components/ClockBar';
import { IconGithub, IconHeart } from './components/Icon';
import { useSession } from './lib/session';
import { rememberIntent } from './lib/intent';

import Login from './routes/Login';
import EmailCode from './routes/EmailCode';
import AuthCallback from './routes/AuthCallback';
import Welcome from './routes/Welcome';
import Now from './routes/Now';
import Schedule from './routes/Schedule';
import EventDetail from './routes/EventDetail';
import MapPage from './routes/MapPage';
import Upload from './routes/Upload';
import Gallery from './routes/Gallery';
import PhotoDetail from './routes/PhotoDetail';
import Credits from './routes/Credits';
import Profile from './routes/Profile';
import AdminGuests from './routes/AdminGuests';
import Scorecard from './routes/Scorecard';
import Icebreakers from './routes/Icebreakers';
import Display from './routes/Display';
import NotFound from './routes/NotFound';

/**
 * The footer, and since D62 the way to the thanks page.
 *
 * Exported for `App.test.tsx`: moving Credits off the header made this a
 * navigation surface rather than a signature, and the link a guest uses to
 * reach a whole route should not be the one untested thing on the page.
 */
export function SiteFoot() {
  return (
    <footer className="site-foot">
      <span>Hand-built by Meg and Videet at unreasonable hours. Nobody else gets your data — not even an app.</span>
      <div style={{ flex: 1 }} />
      {/* Credits lives here since D62 took its header slot. A page of thanks to
          the people who made the week is read once, near the end, and a footer
          is where a website puts that — it is also still linked from Profile,
          so moving it off the nav cost it one way in and left it two. */}
      <Link to="/credits"><IconHeart /> Thanks</Link>
      <a href="https://github.com/megandvideet/site" rel="noreferrer noopener" target="_blank">
        <IconGithub /> github.com/megandvideet/site
      </a>
      {/* Last, and quieter than everything else in here: it is diagnostic
          rather than something a guest is meant to read. The sign-in screens
          carry their own copy — they are routed outside this shell. */}
      <BuildStamp />
    </footer>
  );
}

/**
 * Everything except the login screens is behind a session (D2).
 *
 * Exported for `App.test.tsx`. This function performs the only redirect the
 * roster gate depends on, and it had no test at all — mounting `App` itself
 * would pull in every route in the app and prove nothing about this.
 */
export function Guarded({ children }: { children: React.ReactNode }) {
  const session = useSession();
  const location = useLocation();
  const here = location.pathname + location.search;

  if (!session) {
    // Preserve intent twice over, because the two carriers survive different
    // things. The router state gets a guest who is *already* signed in from the
    // login screen back to where they were going, with no page load in between.
    // The breadcrumb is what survives managed login, which is a full redirect
    // that takes every bit of the first one with it — see `lib/intent`.
    rememberIntent(here);
    return <Navigate to="/" replace state={{ from: here }} />;
  }
  // Both send a guest to the same screen, which shows the claim step first and
  // the name and team reveal after it (D59). `needsClaim` is checked against
  // `=== true` on purpose: it is `undefined` until `GET /me` has answered, and
  // "not asked yet" must not redirect — a boot from cache with no network would
  // otherwise strand every guest on /welcome.
  if ((session.needsWelcome || session.needsClaim === true) && location.pathname !== '/welcome') {
    // Written here rather than handed on in `state`, and written during render
    // rather than in an effect, for the same reason `beginSignIn` writes its
    // verifier this way: the next thing that happens is a navigation, and an
    // effect racing an unmount is not a thing to hang a guest's first
    // impression on. It is an idempotent write of a value already in hand, so
    // a double render in development writes the same string twice.
    rememberIntent(here);
    return <Navigate to="/welcome" replace />;
  }
  return <>{children}</>;
}

function Shell({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  return (
    <div className="app">
      <Header />
      <main className="app-main container">{children}</main>
      {/* A shortcut to the upload screen, from the upload screen, was one more
          thing sitting in the corner the new action bar needs. */}
      {pathname !== '/upload' && <CameraFab />}
      <SiteFoot />
    </div>
  );
}

const guarded = (element: React.ReactNode) => <Guarded><Shell>{element}</Shell></Guarded>;

export default function App() {
  return (
    <>
      {/* Outside `Routes` and outside `Shell`: the sign-in screens are routed
          past the shell, and "which backend is this?" is a question worth
          answering on those screens most of all — signing in against prod from
          localhost is a real Cognito round trip. Dev builds only; see the
          component. */}
      <BackendBadge />
      {/* Beside it, and outside the shell for the same reason: the states most
          worth pinning the clock to are reachable from every screen, including
          the ones routed past `Shell`. Dev builds only; see the component. */}
      <ClockBar />
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/login/email" element={<EmailCode />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/welcome" element={<Guarded><Welcome /></Guarded>} />
        <Route path="/display" element={<Display />} />

        <Route path="/now" element={guarded(<Now />)} />
        <Route path="/schedule" element={guarded(<Schedule />)} />
        <Route path="/schedule/:id" element={guarded(<EventDetail />)} />
        <Route path="/map" element={guarded(<MapPage />)} />
        <Route path="/map/:venueId" element={guarded(<MapPage />)} />
        <Route path="/upload" element={guarded(<Upload />)} />
        <Route path="/gallery" element={guarded(<Gallery />)} />
        <Route path="/gallery/:photoId" element={<Guarded><PhotoDetail /></Guarded>} />
        <Route path="/scorecard" element={guarded(<Scorecard />)} />
        {/* Never gated on the week, unlike the header tab that points at it —
            see `Icebreakers` in lib/content. A link keeps working. */}
        <Route path="/icebreakers" element={guarded(<Icebreakers />)} />
        <Route path="/icebreakers/:cardId" element={guarded(<Icebreakers />)} />
        <Route path="/credits" element={guarded(<Credits />)} />
        <Route path="/me" element={guarded(<Profile />)} />
        <Route path="/me/guests" element={guarded(<AdminGuests />)} />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}
